/**
 * HipaaCheck — decides, right after sign-in, whether the account's
 * HIPAA agreement is accepted and what to show if it is not.
 *
 * Moved from `_legacy/components/Shared/HipaaCheck` with the logic
 * untouched: GET the agreement state; accepted → carry on; not accepted
 * → an Administrator (ACCOUNT_USERS_GET) gets the agreement to accept,
 * anyone else gets the "an Administrator must accept" notice and is
 * moved on. The POST's 500/errorCode 1 ("already accepted") is treated
 * as success on purpose — two admins racing to accept is not an error.
 */
import { useState, useEffect } from 'react';
import { useQuery } from 'react-fetching-library';
import PropTypes from 'prop-types';
import useMetadataUpdate from '@shared/hooks/useMetadataUpdate';
import showServerError from '@shared/lib/showError';
import { checkPermission } from '@shared/lib/checkPermissions';
import permissions from '@shared/config/permissions';
import { useDispatch, useSelector } from 'react-redux';
import HipaaAgreementDialog from './HipaaAgreementDialog';
import HipaaAdminRequiredDialog from './HipaaAdminRequiredDialog';

const HipaaCheck = ({ user, metadata, onSuccess, onFail, onSwitchAccount }) => {
  const dispatch = useDispatch();
  const { errors } = useSelector(state => state.i18n);
  const [isAgreementDialogOpen, setAgreementDialogOpen] = useState(false);
  const [isInfoDialogOpen, setInfoDialogOpen] = useState(false);
  const {
    currentAccountPermissions,
    currentAccount: { uuid, businessName },
  } = metadata;

  const { query: getQuery } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${uuid}/hipaa-agreement`,
    },
    false,
  ]);

  const { query: postQuery } = useMetadataUpdate(useQuery, [
    {
      method: 'POST',
      endpoint: `/accounts/${uuid}/hipaa-agreement`,
    },
    false,
  ]);

  useEffect(() => {
    if (uuid) {
      getQuery().then(({ payload, error }) => {
        if (error) {
          const options = payload && {
            correlationUUID: payload.correlationUUID,
            userUUID: user.uuid,
          };
          showServerError(dispatch, '', options);
          return;
        }
        /*
          policy accepted: redirect to the provided route
          policy not accepted: check permissions
            - true: show HIPAA Agreement
            - false: redirect to the next account / login page
        */
        if (payload) {
          onSuccess();
        } else {
          const hasPermission = checkPermission(
            currentAccountPermissions,
            permissions.ACCOUNT_USERS_GET,
          );
          if (hasPermission) {
            setAgreementDialogOpen(true);
          } else {
            setInfoDialogOpen(true);
          }
        }
      });
    }
  }, []);

  const handleAgreementDialogClose = () => {
    setAgreementDialogOpen(false);
    onFail();
  };

  const handleAgreementDialogSubmit = () => {
    postQuery().then(({ payload, error, status }) => {
      if (error) {
        const options = {
          correlationUUID: payload.correlationUUID,
          userUUID: user.uuid,
        };
        switch (status) {
          case 422: {
            showServerError(dispatch, errors.accountOrUser);
            break;
          }
          case 500: {
            switch (payload.errorCode) {
              case 1: {
                // Agreement already accepted.
                onSuccess();
                break;
              }
              default: {
                showServerError(dispatch, '', options);
                break;
              }
            }
            break;
          }
          default: {
            showServerError(dispatch, '', options);
            break;
          }
        }
      } else {
        onSuccess();
      }
    });
  };

  /*
    redirect user to the next account
    or to the login page
  */
  const handleInfoDialogClose = () => {
    onSwitchAccount();
  };

  return (
    <>
      <HipaaAgreementDialog
        open={isAgreementDialogOpen}
        onClose={handleAgreementDialogClose}
        onSubmit={handleAgreementDialogSubmit}
        accountName={businessName}
      />

      <HipaaAdminRequiredDialog open={isInfoDialogOpen} onClose={handleInfoDialogClose} />
    </>
  );
};

HipaaCheck.propTypes = {
  user: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
  }).isRequired,
  metadata: PropTypes.shape({
    currentAccount: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      businessName: PropTypes.string.isRequired,
    }).isRequired,
    currentAccountPermissions: PropTypes.number.isRequired,
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
  onFail: PropTypes.func.isRequired,
  onSwitchAccount: PropTypes.func.isRequired,
};

export default HipaaCheck;
